import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { assign, createMachine } from "xstate";
import { css, keyframes } from "@nju/css";
import {
  fetchMember,
  IMemberOrchestrators,
  MemberOrchestratorContext,
} from "@nju/data-access-portal-member";
import { IOfferModeWithFSecure } from "@nju/data-access-portal-offers";
import {
  fetchOrchestrators,
  getOrchestratorForMode,
  IFtthOrchestrator,
  IMultiOrderOrchestrator,
  IOrchestrators,
  OrchestratorsContext,
} from "@nju/data-access-portal-orchestrator";
import { BasicError } from "@nju/result";
import { portalRoutes } from "@nju/routes";
import { CircleLoader, GeneralErrorNotification } from "@nju/ui";
import { redirectToOrchestratorState } from "./redirectToOrchestratorState";
import { resolvePathFromOrchestratorState } from "./resolvePathFromOrchestratorState";
import { RespectMode } from "./RespectMode";

type IStateOrStates =
  | IFtthOrchestrator["state"]
  | IMultiOrderOrchestrator["state"]
  | "?"
  | Array<IFtthOrchestrator["state"] | "?">
  | Array<IMultiOrderOrchestrator["state"] | "?">;

interface IRespectOrchestratorStateProps {
  state: IStateOrStates;
  forceIgnoreProperStateParamName?: string;
  refreshInterval?: number;
  mode: IOfferModeWithFSecure;
  allowMissingState?: boolean;
}

interface IContext {
  orchestrators?: IOrchestrators;
  memberOrchestrators?: IMemberOrchestrators;
  refreshInterval?: number;
  allowMissingState?: boolean;
  mode: IOfferModeWithFSecure;
  stateOrStates: IStateOrStates;
  shouldIgnoreProperState?: boolean;
  isPristine: boolean;
}

type IEvent =
  | { type: "RELOAD" }
  | { type: "GENERAL_ERROR" }
  | { type: "OK"; orchestrators: IOrchestrators }
  | { type: "MISSING_ORCHESTRATOR" }
  | {
      type: "FETCH_MEMBER_SUCCESS";
      memberOrchestrators: IMemberOrchestrators;
    }
  | {
      type: "INVALID_STATE";
      state: IFtthOrchestrator["state"] | IMultiOrderOrchestrator["state"];
    };

const machine = createMachine<IContext, IEvent>(
  {
    context: {
      mode: "ftth",
      stateOrStates: "?",
      isPristine: true,
    },
    initial: "load",
    states: {
      idle: {
        on: {
          RELOAD: {
            target: "load",
          },
        },
        after: [
          {
            delay: (context) => {
              return context.refreshInterval || Number.POSITIVE_INFINITY;
            },
            cond: (context) => {
              return !!context.refreshInterval;
            },
            target: "load",
          },
        ],
      },
      load: {
        invoke: {
          src: ({
            shouldIgnoreProperState,
            allowMissingState,
            mode,
            stateOrStates,
          }) => {
            return async (callback) => {
              const result = await fetchOrchestrators();

              if (result.isError) {
                return callback({ type: "GENERAL_ERROR" });
              }

              const orchestrator = getOrchestratorForMode(result.value, mode);

              if (shouldIgnoreProperState) {
                return callback({ type: "OK", orchestrators: result.value });
              }

              if (allowMissingState && !orchestrator) {
                return callback({ type: "OK", orchestrators: result.value });
              }

              if (!orchestrator) {
                return callback({ type: "MISSING_ORCHESTRATOR" });
              }

              if (checkIfStateIsCorrect(stateOrStates, orchestrator.state)) {
                return callback({ type: "OK", orchestrators: result.value });
              }

              console.warn(
                `[nju][RespectOrchestratorState:invalid-state]: ${stateOrStates} vs ${
                  orchestrator.state
                } => ${resolvePathFromOrchestratorState({
                  state: orchestrator.state,
                })}`,
              );

              return callback({
                type: "INVALID_STATE",
                state: orchestrator.state,
              });
            };
          },
        },
        on: {
          GENERAL_ERROR: {
            target: "error",
          },
          INVALID_STATE: {
            actions: "onRedirectToOrchestratorState",
            target: "invalidState",
          },
          OK: [
            {
              actions: "orchestratorResolved",
              target: "member",
            },
          ],
          MISSING_ORCHESTRATOR: {
            actions: "onMissingOrchestrator",
          },
        },
      },
      member: {
        invoke: {
          src: "fetchMember",
        },
        on: {
          FETCH_MEMBER_SUCCESS: {
            actions: "setMember",
            target: "idle",
          },
          GENERAL_ERROR: {
            target: "error",
          },
        },
      },
      error: {},
      invalidState: {},
    },
  },
  {
    services: {
      fetchMember: () => {
        return async (callback) => {
          try {
            const { group } = (await fetchOrchestrators()).unwrap();

            if (group?.payload?.userId) {
              const result = (
                await fetchMember({
                  userId: group.payload.userId,
                })
              ).unwrap();

              return callback({
                type: "FETCH_MEMBER_SUCCESS",
                memberOrchestrators: result,
              });
            }

            return callback({
              type: "FETCH_MEMBER_SUCCESS",
              memberOrchestrators: {
                mobile: undefined,
                changeMsisdn: undefined,
                mobileReactivation: undefined,
                tv: undefined,
                changeSimEsim: undefined,
              },
            });
          } catch (error) {
            if (error instanceof BasicError) {
              return callback({ type: "GENERAL_ERROR" });
            }
          }
        };
      },
    },
    actions: {
      orchestratorResolved: assign((context, event) => {
        if (event.type === "OK") {
          return {
            orchestrators: event.orchestrators,
            isPristine: false,
          };
        }

        return context;
      }),
      setMember: assign((context, event) => {
        if (event.type === "FETCH_MEMBER_SUCCESS") {
          return {
            memberOrchestrators: event.memberOrchestrators,
          };
        }

        return context;
      }),
    },
  },
);

function checkIfStateIsCorrect(allowed: string | string[], provided: string) {
  return typeof allowed === "string"
    ? allowed === provided
    : allowed.includes(provided);
}

function RespectOrchestratorStateInternal({
  state: stateOrStates,
  mode,
  forceIgnoreProperStateParamName = "zmien",
  children,
  refreshInterval,
  allowMissingState = false,
}: React.PropsWithChildren<IRespectOrchestratorStateProps>) {
  const router = useRouter();

  const [current] = useMachine(machine, {
    actions: {
      onMissingOrchestrator: () => {
        router.push(portalRoutes.mojeKonto.index.get({ mode }));
        return;
      },
      onRedirectToOrchestratorState: (_, event) => {
        if (event.type === "INVALID_STATE") {
          redirectToOrchestratorState({
            state: event.state,
            mode,
            ...(mode === "mobile" && { flow: "multiOrder" }),
          });
        }
      },
    },
    context: {
      refreshInterval,
      allowMissingState,
      mode,
      stateOrStates,
      shouldIgnoreProperState: forceIgnoreProperStateParamName in router.query,
    },
  });

  const { context, matches } = current;
  const { orchestrators, isPristine, memberOrchestrators } = context;

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return router.push(portalRoutes.index.get());
        }}
      />
    );
  }

  if (
    isPristine ||
    matches("invalidState") ||
    orchestrators === undefined ||
    memberOrchestrators === undefined
  ) {
    return (
      <div className={loaderWrapper()}>
        <CircleLoader />
      </div>
    );
  }

  return (
    <OrchestratorsContext.Provider value={orchestrators}>
      <MemberOrchestratorContext.Provider value={memberOrchestrators}>
        {children}
      </MemberOrchestratorContext.Provider>
    </OrchestratorsContext.Provider>
  );
}

function RedirectToOrchestratorState(
  props: React.PropsWithChildren<{ mode: IOfferModeWithFSecure }>,
) {
  const router = useRouter();

  React.useEffect(() => {
    const result = portalRoutes.zaloguj.kontynuuj.input(router.query);

    if (result.isOk) {
      redirectToOrchestratorState({
        state: result.value.redirect,
        mode: props.mode,
      });
      return;
    }
  }, [props.mode, router.query]);

  return <>{props.children}</>;
}

export function RespectOrchestratorState(
  props: React.PropsWithChildren<
    Omit<IRespectOrchestratorStateProps, "mode"> & {
      mode: IOfferModeWithFSecure | IOfferModeWithFSecure[];
    }
  >,
) {
  return (
    <RespectMode mode={props.mode}>
      {({ mode }) => {
        return (
          <RedirectToOrchestratorState mode={mode}>
            <RespectOrchestratorStateInternal {...props} mode={mode} />
          </RedirectToOrchestratorState>
        );
      }}
    </RespectMode>
  );
}

const show = keyframes({
  "0%": { opacity: 0 },
  "1%": { opacity: 0 },
  "100%": { opacity: 1 },
});

const loaderWrapper = css({
  opacity: 0,
  display: "flex",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  animationName: `${show}`,
  animationDelay: "0.3s",
  animationDuration: "1s",
  animationFillMode: "forwards",
});
